import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert } from "@/integrations/supabase/types";
import { planToFrequency } from "@/lib/planFrequency";
import { ensureShootingFolder } from "./rushesVirtualFolders";

export type Shooting = Tables<"shootings">;
export type ShootingInsert = TablesInsert<"shootings">;
export type ShootingWithBookings = Shooting & { bookings?: any[] };
export interface CreateShootingParams {
  userId: string;
  title?: string;
  productId?: string;
  locationId?: string;
  plan?: string;
}

/**
 * Create a new shooting
 */
export const createShooting = async (params: CreateShootingParams) => {
  const { userId, title, productId, locationId, plan } = params;
  
  try {
    const { data, error } = await supabase
      .from("shootings")
      .insert({
        user_id: userId,
        title: title || null,
        status: 'scheduled',
        product_id: productId || null,
        location_id: locationId || null,
        plan: plan || null,
      })
      .select()
      .single();

    if (error) throw error;
    
    // Fire-and-forget: create virtual folder for this shooting
    ensureShootingFolder(data.id, userId, data.date ?? null).catch(err =>
      console.warn('[createShooting] ensureShootingFolder failed (non-blocking):', err)
    );
    
    return { success: true, data };
  } catch (error) {
    return { success: false, error };
  }
};

/**
 * Get all shootings for a user
 */
export const getShootings = async (userId: string) => {
  try {
    const { data: shootings, error: shootingsError } = await supabase
      .from("shootings")
      .select(`
        *,
        products (*),
        locations (*)
      `)
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (shootingsError) throw shootingsError;
    
    // Fetch bookings for each shooting
    if (shootings && shootings.length > 0) {
      const shootingIds = shootings.map(s => s.id);
      const { data: bookings, error: bookingsError } = await supabase
        .from("bookings")
        .select(`
          id,
          start_time,
          end_time,
          meeting_type,
          director_id,
          personal_brand_manager_id,
          shooting_id
        `)
        .in("shooting_id", shootingIds);
      
      if (bookingsError) {
        console.warn("[getShootings] Error fetching bookings:", bookingsError);
      } else {
        // Attach bookings to shootings
        shootings.forEach((shooting: ShootingWithBookings) => {
          shooting.bookings = bookings?.filter(b => b.shooting_id === shooting.id) || [];
        });
      }
    }
    
    return { success: true, data: shootings };
  } catch (error) {
    console.error("[getShootings] Error:", error);
    return { success: false, error };
  }
};

export interface ShootingWithContentCount {
  id: string;
  title: string | null;
  date: string | null;
  plan: string | null;
  status: string | null;
  content_count: number;
}

/**
 * Get shootings for a user with validated video_topics count (for admin list modal).
 */
export const getShootingsForUserWithContentCount = async (
  userId: string
): Promise<{ success: boolean; data: ShootingWithContentCount[]; error?: unknown }> => {
  try {
    const { data: shootingsData, error: shootingsError } = await supabase
      .from("shootings")
      .select("id, title, date, plan, status")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (shootingsError) throw shootingsError;
    if (!shootingsData || shootingsData.length === 0) {
      return { success: true, data: [] };
    }

    const shootingIds = shootingsData.map((s) => s.id);
    const { data: topicsData } = await supabase
      .from("video_topics")
      .select("shooting_id")
      .in("shooting_id", shootingIds)
      .eq("status", "validated");

    const countByShooting: Record<string, number> = {};
    shootingIds.forEach((id) => {
      countByShooting[id] = 0;
    });
    if (topicsData) {
      topicsData.forEach((t) => {
        countByShooting[t.shooting_id] = (countByShooting[t.shooting_id] ?? 0) + 1;
      });
    }

    const data: ShootingWithContentCount[] = shootingsData.map((s) => ({
      id: s.id,
      title: s.title,
      date: s.date,
      plan: s.plan,
      status: s.status,
      content_count: countByShooting[s.id] ?? 0,
    }));

    return { success: true, data };
  } catch (error) {
    console.error("[getShootingsForUserWithContentCount] Error:", error);
    return { success: false, data: [], error };
  }
};

/**
 * Get a shooting by ID with all related data
 */
export const getShootingById = async (id: string) => {
  try {
    const { data: shooting, error: shootingError } = await supabase
      .from("shootings")
      .select(`
        *,
        products (*),
        locations (*)
      `)
      .eq("id", id)
      .maybeSingle();

    if (shootingError) throw shootingError;
    
    if (!shooting) {
      return { success: true, data: null };
    }
    
    // Fetch bookings for this shooting
    const { data: bookings, error: bookingsError } = await supabase
      .from("bookings")
      .select(`
        id,
        start_time,
        end_time,
        meeting_type,
        director_id,
        personal_brand_manager_id
      `)
      .eq("shooting_id", id);
    
    if (bookingsError) {
      console.warn("[getShootingById] Error fetching bookings:", bookingsError);
    } else {
      const shootingWithBookings = shooting as ShootingWithBookings;
      shootingWithBookings.bookings = bookings || [];
    }
    
    return { success: true, data: shooting };
  } catch (error) {
    console.error("[getShootingById] Error:", error);
    return { success: false, error };
  }
};

/**
 * Update a shooting
 */
export const updateShooting = async (
  id: string,
  updates: Partial<Shooting>
) => {
  try {
    const { data, error } = await supabase
      .from("shootings")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("[updateShooting] Error:", error);
    return { success: false, error };
  }
};

/**
 * Delete a shooting
 */
export const deleteShooting = async (id: string) => {
  try {
    const { error } = await supabase
      .from("shootings")
      .delete()
      .eq("id", id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error("[deleteShooting] Error:", error);
    return { success: false, error };
  }
};

/**
 * Link a booking to a shooting
 */
export const linkBookingToShooting = async (
  shootingId: string,
  bookingId: string
) => {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({ shooting_id: shootingId })
      .eq("id", bookingId);

    if (error) throw error;
    
    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

/**
 * Unlink a booking from a shooting
 */
export const unlinkBookingFromShooting = async (
  shootingId: string,
  bookingId: string
) => {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({ shooting_id: null })
      .eq("id", bookingId)
      .eq("shooting_id", shootingId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error("[unlinkBookingFromShooting] Error:", error);
    return { success: false, error };
  }
};

/**
 * Get shooting by booking ID (find which shooting a booking belongs to)
 */
export const getShootingByBookingId = async (bookingId: string) => {
  try {
    const { data: booking, error } = await supabase
      .from("bookings")
      .select(`
        shooting_id,
        shootings (*)
      `)
      .eq("id", bookingId)
      .maybeSingle();

    if (error) throw error;
    return { success: true, data: booking?.shootings || null };
  } catch (error) {
    console.error("[getShootingByBookingId] Error:", error);
    return { success: false, error };
  }
};

export interface ShootingForAssignment {
  id: string;
  title: string | null;
  date: string | null;
  status: string | null;
}

/**
 * Get real shootings for a user — lightweight, dedicated to the assignment selector.
 * Excludes legacy "Tournage #X" artefacts from analytics imports.
 */
export const getShootingsForAssignment = async (
  userId: string
): Promise<{ success: boolean; data: ShootingForAssignment[]; error?: unknown }> => {
  try {
    const { data, error } = await supabase
      .from("shootings")
      .select("id, title, date, status")
      .eq("user_id", userId)
      .order("date", { ascending: false });

    if (error) throw error;

    // Filter out legacy analytics artefacts ("Tournage #X")
    const filtered = (data ?? []).filter(
      (s) => !s.title || !/^Tournage #\d+$/i.test(s.title)
    );

    return { success: true, data: filtered };
  } catch (error) {
    console.error("[getShootingsForAssignment] Error:", error);
    return { success: false, data: [], error };
  }
};

/**
 * Update plan for all shootings of a user
 * @param userId - User ID
 * @param plan - New plan value
 * @param options - Optional settings
 * @param options.oldPlan - Previous plan (if provided, will trigger notification when different)
 * @param options.skipNotification - If true, skip sending notification
 */
export const updateUserShootingsPlan = async (
  userId: string, 
  plan: string,
  options?: { oldPlan?: string | null; skipNotification?: boolean }
) => {
  try {
    const { data, error } = await supabase
      .from("shootings")
      .update({ plan })
      .eq("user_id", userId)
      .select();

    if (error) throw error;

    // Synchronize global_frequency in user_planning_preferences
    const frequency = planToFrequency(plan);
    const { error: prefError } = await supabase
      .from("user_planning_preferences")
      .upsert({
        user_id: userId,
        global_frequency: frequency,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id' });

    if (prefError) {
      console.warn("[updateUserShootingsPlan] Failed to sync global_frequency:", prefError);
    } else {
      console.log("[updateUserShootingsPlan] Synced global_frequency to", frequency, "for plan", plan);
    }

    // Send notification if plan changed (and old plan was provided)
    const shouldNotify = options?.oldPlan && 
                         options.oldPlan !== plan && 
                         !options?.skipNotification;
    
    if (shouldNotify) {
      try {
        await supabase.functions.invoke('notify-plan-change', {
          body: {
            user_id: userId,
            new_plan: plan,
            old_plan: options.oldPlan
          }
        });
      } catch (notifError) {
        // Don't fail the whole operation if notification fails, but log the error
        console.warn("[updateUserShootingsPlan] Failed to send plan change notification:", notifError);
      }
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error };
  }
};

